// ElectionModeDetails.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card, Spin, message, Button, Tag } from "antd";
import { useJsApiLoader } from "@react-google-maps/api";
import useAxiosInstance from "../../lib/useAxiosInstance";

import ElectionModeMapView from "./ElectionModeMapView";

const ElectionModeDetails = () => {
  const { id } = useParams();
  const axiosInstance = useAxiosInstance();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [polygonPaths, setPolygonPaths] = useState([]);

  const [isFullScreen, setIsFullScreen] = useState(false);

  const mainMapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    fetchDetails();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildPolygonPaths = (shapeList = []) => {
    const allPolygons = [];

    shapeList.forEach((shape) => {
      const { boundaries, name } = shape || {};

      if (!boundaries?.type || !boundaries?.coordinates) return;

      if (boundaries.type === "MultiPolygon") {
        boundaries.coordinates.forEach((poly) => {
          const ring = poly[0] || [];

          const coordinates = ring
            .map(([lng, lat]) => ({
              lat: Number(lat),
              lng: Number(lng),
            }))
            .filter((c) => !isNaN(c.lat) && !isNaN(c.lng));

          if (coordinates.length) {
            allPolygons.push({
              name,
              coordinate: coordinates,
            });
          }
        });
      }

      if (boundaries.type === "Polygon") {
        boundaries.coordinates.forEach((ring) => {
          const coordinates = ring
            .map(([lng, lat]) => ({
              lat: Number(lat),
              lng: Number(lng),
            }))
            .filter((c) => !isNaN(c.lat) && !isNaN(c.lng));

          if (coordinates.length) {
            allPolygons.push({
              name,
              coordinate: coordinates,
            });
          }
        });
      }
    });

    return allPolygons;
  };

  const fetchDetails = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(`api/electionMode/${id}`);

      const electionData = res.data?.data;

      setData(electionData);

      if (electionData?.isShapeExist) {
        try {
          const shapeRes = await axiosInstance.get(
            `/api/electionMode/getShapeFile/${id}`
          );

          const rawShapes = shapeRes.data?.data || [];

          const polys = buildPolygonPaths(rawShapes);

          setPolygonPaths(polys);
        } catch (shapeErr) {
          console.error(shapeErr);

          setPolygonPaths([]);
        }
      } else {
        setPolygonPaths([]);
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to load election mode details");
    } finally {
      setLoading(false);
    }
  };

  const clientLabel = (client) =>
    client
      ? `${client.adminFirstName || ""} ${
          client.adminLastName || ""
        }`.trim() ||
        client.organisationName ||
        client.adminEmail
      : "";

  const defaultCenter = useMemo(() => {
    if (polygonPaths[0]?.coordinate?.length) {
      return polygonPaths[0].coordinate[0];
    }

    return { lat: 26.8467, lng: 80.9462 };
  }, [polygonPaths]);

  if (loadError) {
    return (
      <div style={{ padding: 24 }}>
        <p>Failed to load map</p>
      </div>
    );
  }

  if (loading || !data || !isLoaded) {
    return (
      <div style={{ padding: 24 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card title={data.electionModeName} style={{ marginBottom: 24 }}>
        <p>
          <strong>Description:</strong> {data.description}
        </p>

        <p>
          <strong>Election Mode Type:</strong>{" "}
          <Tag color="blue">{data.electionModeType}</Tag>
        </p>

        <p>
          <strong>Client:</strong> {clientLabel(data.client)}
        </p>

        <p>
          <strong>Form20 Files:</strong>{" "}
          {data.form20Files?.length || 0}
        </p>

        <p>
          <strong>Electoral Roll Files:</strong>{" "}
          {data.erFiles?.length || 0}
        </p>
      </Card>

      <Card
        title="Election Mode Map"
        extra={
          <Button
            type="primary"
            className="bg-[#004877]"
            onClick={() => setIsFullScreen(true)}
          >
            Open Full Screen
          </Button>
        }
      >
        <div
          className={`relative transition-all duration-300 ${
            isFullScreen
              ? "fixed inset-0 z-[9999] bg-black/80 p-0"
              : ""
          }`}
        >
          <div className={`${isFullScreen ? "h-full" : "h-[500px]"} w-full`}>
            <ElectionModeMapView
              isLoaded={isLoaded}
              open={isFullScreen}
              setIsFullScreen={setIsFullScreen}
              id={id}
              center={defaultCenter}
              polygonPaths={polygonPaths}
              mapRef={mainMapRef}
              electionMode={data}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ElectionModeDetails;
